import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { sellerService } from '@/services/seller';
import { useSellerStore } from '@/store/seller';
import { StoreReviewsPage as ConsumerStoreReviewsPage } from '@/pages/store/StoreReviewsPage';

/**
 * 판매자용 데스크톱 리뷰 관리 페이지
 * 판매자가 자신의 매장 리뷰를 확인하고 관리하는 컴포넌트
 * 단일책임: 판매자의 리뷰 관리 기능만 담당
 */
export function DesktopSellerStoreReviewsPage() {
  const navigate = useNavigate();
  const { setLoading, setError } = useSellerStore();
  
  const [restaurantId, setRestaurantId] = useState<string>('');

  useEffect(() => {
    loadRestaurantId();
  }, []);

  const loadRestaurantId = async () => {
    try {
      setLoading(true);
      
      const restaurantResult = await sellerService.getSellerRestaurantId();
      if (!restaurantResult.success || !restaurantResult.restaurantId) {
        setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
        return;
      }

      setRestaurantId(restaurantResult.restaurantId);
      
    } catch (error) {
      console.error('Restaurant ID load error:', error);
      setError('레스토랑 정보를 불러오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  if (!restaurantId) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">레스토랑 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-5xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-800 mr-4"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-[32px] font-bold text-gray-800">리뷰 관리</h1>
        </div>

        {/* Reviews Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[600px]">
          {/* 안내 메시지 */}
          <div className="bg-blue-50 border-b border-blue-200 p-6">
            <h2 className="text-[20px] font-semibold text-blue-800 mb-2">고객 리뷰 관리</h2>
            <p className="text-[16px] text-blue-700">
              고객들이 남긴 리뷰를 확인하고 관리할 수 있습니다. 부적절한 리뷰는 신고 기능을 통해 관리자에게 문의하세요.
            </p>
          </div>

          {/* 임베드된 소비자용 리뷰 페이지 (읽기 전용) */}
          <div className="p-6">
            <ConsumerStoreReviewsPage storeId={restaurantId} readonly={true} />
          </div>
        </div>

        {/* 추가 안내 정보 */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="text-[18px] font-semibold text-green-800 mb-3">리뷰 관리 팁</h3>
            <ul className="space-y-2 text-[16px] text-green-700">
              <li>• 긍정적인 리뷰에는 감사 인사를 전달하세요</li>
              <li>• 부정적인 리뷰는 개선의 기회로 활용하세요</li>
              <li>• 정기적으로 리뷰를 확인하여 서비스 품질을 향상시키세요</li>
            </ul>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
            <h3 className="text-[18px] font-semibold text-orange-800 mb-3">신고 안내</h3>
            <ul className="space-y-2 text-[16px] text-orange-700">
              <li>• 부적절한 리뷰는 신고 버튼을 통해 문의하세요</li>
              <li>• 허위 사실이나 악의적인 리뷰는 관리자가 검토합니다</li>
              <li>• 신고 처리까지 1-3일 정도 소요됩니다</li>
            </ul>
          </div>
        </div>
      </div>
    </DesktopSellerPageContainer>
  );
}